import { defaultCiphers } from "../schema/types/domain/entity";
import { CoralogixLoggingCluster, DatadogLoggingHost, LogzioLoggingListenerHost } from "../schema/types/org/logging";
import { ConcurrencyPolicy, RestartPolicy, WorkloadType } from "../schema/types/workload/entity";
import { OptionsMetricPercentile } from "../schema/types/workload/workloadOptions";

export const defaultValues = {
  pullSecretTypes: ["gcp", "docker", "ecr"],
  networkResource: {
    discover: "ip", // ip or fqdn
  },
  nativeNetworkResource: {
    provider: "aws", // aws, gcp
  },
  // up not processed
  workload: {
    type: {
      default: "standard" as WorkloadType,
      options: ["standard", "stateful", "cron", "serverless"] as WorkloadType[],
      selectOptions: [
        { label: "Standard", value: "standard" },
        { label: "Stateful", value: "stateful" },
        { label: "Cron", value: "cron" },
        { label: "Serverless", value: "serverless" },
      ],
    },
    options: {
      autoscaling: {
        metricPercentile: {
          default: "p50" as OptionsMetricPercentile,
        },
        target: {
          default: "95",
          min: 1,
        },
        minScale: {
          min: 0,
          default: "1",
        },
        maxScale: {
          min: 0,
          default: "5",
        },
        scaleToZeroDelay: {
          min: 30,
          max: 3600,
          default: "300",
        },
        maxConcurrency: {
          min: 0,
          max: 30000,
          default: "0",
        },
      },
      timeoutSeconds: {
        min: 1,
        max: 60 * 60,
        default: "5",
      },
    },
    securityOptions: {
      filesystemGroupId: {
        min: 1,
        max: 65534,
      },
    },
    rolloutOptions: {
      minReadySeconds: {
        default: "0",
      },
      maxSurgeReplicas: {
        default: "25%",
      },
      scalingPolicy: {
        default: "OrderedReady",
      },
      terminationGracePeriodSeconds: {
        min: 0,
        max: 900,
        default: "90",
      },
    },
    container: {
      port: {
        protocol: "http",
        number: 8080,
      },
      gpu: {
        default: "t4",
        options: ["t4", "a10g"],
      },
      cpu: {
        default: "50m",
      },
      memory: {
        default: "128Mi",
      },
    },
    job: {
      schedule: {
        default: "* * * * *",
      },
      concurrencyPolicy: {
        default: "Forbid" as ConcurrencyPolicy,
      },
      restartPolicy: {
        default: "Never" as RestartPolicy,
      },
      historyLimit: {
        default: "5",
        min: 1,
        max: 10,
      },
      activeDeadlineSeconds: {
        min: 1,
        max: 86400,
      },
    },
    requestRetryPolicy: {
      attempts: {
        min: 0,
        default: "2",
        max: Math.pow(2, 32) - 1,
      },
      retryOn: {
        options: [
          "500",
          "501",
          "502",
          "503",
          "504",
          "gateway-error",
          "reset",
          "reset-before-request",
          "connect-failure",
          "envoy-ratelimited",
          "retriable-400",
          "retriable-401",
          "retriable-403",
          "retriable-404",
          "retriable-405",
          "retriable-409",
          "retriable-429",
          "refused-stream",
          "retriable-status-codes",
          "http3-post-connect-failure",
          // ---
          "cancelled",
          "deadline-exceeded",
          "internal",
          "resource-exhausted",
          "unavailable",
        ],
        default: [
          "connect-failure",
          "refused-stream",
          "unavailable",
          "cancelled",
          "resource-exhausted",
          "retriable-status-codes",
        ],
      },
    },
  },
  identity: {
    ngs: {
      resp: {
        max: {
          min: -1,
          default: "1",
        },
      },
      subs: {
        min: -1,
        default: "-1",
      },
      data: {
        min: -1,
        default: "-1",
      },
      payload: {
        min: -1,
        default: "-1",
      },
    },
  },
  org: {
    sessionTimeoutSeconds: {
      default: String(15 * 60),
    },
    logging: {
      cloudWatch: {
        region: {
          options: [
            "af-south-1",
            "ap-northeast-1",
            "ap-northeast-2",
            "ap-south-1",
            "ap-southeast-1",
            "ap-southeast-2",
            "ca-central-1",
            "eu-central-1",
            "eu-north-1",
            "eu-south-1",
            "eu-west-1",
            "eu-west-2",
            "eu-west-3",
            "me-south-1",
            "sa-east-1",
            "us-east-1",
            "us-east-2",
            "us-west-1",
            "us-west-2",
          ],
        },
        retentionDays: {
          options: [1, 3, 5, 7, 14, 30, 60, 90, 120, 150, 180, 365, 400, 545, 731, 1827, 3653],
        },
      },
      coralogix: {
        cluster: {
          default: "coralogix.com" as CoralogixLoggingCluster,
          options: [
            "coralogix.com",
            "coralogix.us",
            "app.coralogix.in",
            "app.eu2.coralogix.com",
            "app.coralogixsg.com",
          ],
        },
      },
      datadog: {
        host: {
          default: "http-intake.logs.datadoghq.com" as DatadogLoggingHost,
          options: [
            "http-intake.logs.datadoghq.com",
            "http-intake.logs.us3.datadoghq.com",
            "http-intake.logs.us5.datadoghq.com",
            "http-intake.logs.datadoghq.eu",
          ],
        },
      },
      elastic: {
        aws: {
          port: {
            default: "443",
          },
        },
        generic: {
          port: {
            default: "443",
          },
        },
      },
      fluentd: {
        port: { min: 1, max: 65535, default: "24224" },
      },
      logzio: {
        listenerHost: {
          default: "listener.logz.io" as LogzioLoggingListenerHost,
          options: ["listener.logz.io", "listener-nl.logz.io"],
        },
      },
      stackdriver: {
        region: {
          options: [
            "us-east1",
            "us-east4",
            "us-central1",
            "us-west1",
            "europe-west4",
            "europe-west1",
            "europe-west3",
            "europe-west2",
            "asia-east1",
            "asia-southeast1",
            "asia-northeast1",
            "asia-south1",
            "australia-southeast1",
            "southamerica-east1",
            "africa-south1",
            "asia-east2",
            "asia-northeast2",
            "asia-northeast3",
            "asia-south2",
            "asia-southeast2",
            "australia-southeast2",
            "europe-central2",
            "europe-north1",
            "europe-southwest1",
            "europe-west10",
            "europe-west12",
            "europe-west6",
            "europe-west8",
            "europe-west9",
            "me-central1",
            "me-central2",
            "me-west1",
            "northamerica-northeast1",
            "northamerica-northeast2",
            "southamerica-west1",
            "us-east5",
            "us-south1",
            "us-west2",
            "us-west3",
            "us-west4",
          ],
        },
      },
      syslog: {
        mode: {
          default: "tcp",
          options: ["tcp", "tls", "udp"],
          selectOptions: [
            { label: "TCP", value: "tcp" },
            { label: "TLS", value: "tls" },
            { label: "UDP", value: "udp" },
          ],
        },
        severity: {
          default: "6",
        },
        format: {
          default: "rfc3164",
          options: ["rfc3164", "rfc5424"],
          selectOptions: [
            { label: "RFC3164", value: "rfc3164" },
            { label: "RFC5424", value: "rfc5424" },
          ],
        },
        port: { default: "24224" },
      },
    },
    threatDetection: {
      syslog: {
        transport: {
          default: "tcp",
          options: ["tcp", "udp"],
          selectOptions: [
            { label: "TCP", value: "tcp" },
            { label: "UDP", value: "udp" },
          ],
        },
      },
      minimumSeverity: {
        default: "warning",
        options: ["warning", "error", `critical`],
        selectOptions: [
          { label: "Warning", value: "warning" },
          { label: "Error", value: "error" },
          { label: "Critical", value: "critical" },
        ],
      },
    },
    observability: {
      logsRetentionDays: {
        min: 0,
        default: 30,
      },
      metricsRetentionDays: {
        min: 0,
        default: 30,
      },
      tracesRetentionDays: {
        min: 0,
        default: 30,
      },
    },
  },
  // not processed
  domain: {
    value: "none",
    cors: {
      maxAge: "24h",
      allowCredentials: false,
    },
    tls: {
      cipherSuites: defaultCiphers,
      minProtocolVersion: "TLSV1_2",
    },
    port: {
      number: {
        options: [443, 80, 1443, 3000, 3001, 3002, 3003, 3004, 5912, 8443, 8444, 50051, 50052, 5912],
      },
      protocol: {
        options: ["http", "http2", "tcp"],
      },
    },
  },
  gvc: {
    endpointNamingFormat: {
      default: "org",
      options: ["org", "default"],
      selectOptions: [
        {
          label: "Org",
          value: "org",
        },
        { label: "Default", value: "default" },
      ],
    },
    loadBalancer: {
      trustedProxies: {
        default: "0",
        min: 0,
        max: 2,
      },
    },
  },
  // Used by both org and gvc
  tracing: {
    sampling: {
      default: "50",
      min: 0,
      max: 100,
    },
  },
  volumeset: {
    initialCapacity: {
      min: 10,
      default: "10",
      selectOptions: [
        {
          label: "10 GB",
          value: "10",
        },
        {
          label: "20 GB",
          value: "20",
        },
        {
          label: "50 GB",
          value: "50",
        },
        {
          label: "100 GB",
          value: "100",
        },
      ],
    },
    performanceClass: {
      default: "general-purpose-ssd",
      options: ["general-purpose-ssd", "high-throughput-ssd"],
    },
    fileSystemType: {
      default: "ext4",
      options: ["xfs", "ext4", "shared"],
    },
    snapshots: {
      retentionDuration: {
        amount: {
          min: 1,
          default: "7",
        },
        unit: {
          default: "d",
          options: ["m", "h", "d"],
          selectOptions: [
            {
              value: "m",
              label: "Minutes",
            },
            {
              value: "h",
              label: "Hours",
            },
            {
              value: "d",
              label: "Days",
            },
          ],
        },
      },
    },
    autoscaling: {
      maxCapacity: {
        min: 10,
      },
      minFreePercentage: {
        min: 1,
        max: 100,
      },
      scalingFactor: {
        min: 1.1,
      },
    },
    mountOptions: {
      resources: {
        minCpu: {
          amount: { default: "500" },
          unit: { default: "m" },
        },
        maxCpu: {
          amount: { default: "2000" },
          unit: { default: "m" },
        },
        minMemory: {
          amount: { default: "1" },
          unit: { default: "Gi" },
        },
        maxMemory: {
          amount: { default: "2" },
          unit: { default: "Gi" },
        },
      },
    },
  },
  // not processed
  mk8s: {
    firewall: [
      {
        sourceCIDR: "0.0.0.0/0",
        description: "Default allow-all rule",
      },
    ],
    provider: {
      hetzner: {
        image: "ubuntu-20.04",
      },
      common: {
        autoscaler: {
          expander: ["most-pods"],
          unneededTime: "10m",
          unreadyTime: "20m",
          utilizationThreshold: 0.7,
        },
      },
    },
  },
};

export function toSelectOptions(options: (string | number)[]) {
  return options.map((option) => ({ label: String(option), value: String(option) }));
}
